import React from 'react';
import PropTypes from 'prop-types';
import { Modal, Button } from 'react-bootstrap';
import styledComponent from '../../../utils/styledComponent';
import theme from '../../../theme';

// Confirmation gate for the carryover NextMV run (SCT-692 / mock 010):
// shows what will and won't be replanned before sending. Auto-assign stops
// are what the reattempt run sends; specific-route stops are staged through
// the Route Editor and listed here only so the review is complete.

const StyledDiv = styledComponent('div', {
  '& .crm-subtitle': {
    fontSize: theme.typography.label.fontSize,
    color: theme.colors.textMuted,
    marginBottom: theme.spacing.fieldGap,
  },
  '& .crm-group-label': {
    fontSize: theme.typography.microLabel.fontSize,
    fontWeight: theme.typography.microLabel.fontWeight,
    letterSpacing: theme.typography.microLabel.letterSpacing,
    textTransform: 'uppercase',
    marginBottom: theme.spacing.labelGap,
  },
  '& .crm-group-label-reroute': {
    color: theme.colors.successText,
  },
  '& .crm-group-label-skip': {
    color: theme.colors.textMuted,
    marginTop: theme.spacing.fieldGap,
  },
  '& .crm-row': {
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing.gapXl,
    padding: `${theme.spacing.gapMd} 0`,
    borderBottom: `1px solid ${theme.colors.borderLight}`,
    fontSize: theme.typography.label.fontSize,
    color: theme.colors.textDark,
  },
  '& .crm-row:last-of-type': {
    borderBottom: 'none',
  },
  '& .crm-row-right': {
    textAlign: 'right',
  },
  '& .crm-tag-auto': {
    color: theme.colors.primary,
    fontWeight: theme.fontWeights.semibold,
  },
  '& .crm-tag-specific': {
    color: theme.colors.textMuted,
  },
  '& .crm-tag-missing': {
    color: theme.colors.danger,
    fontWeight: theme.fontWeights.semibold,
  },
  '& .crm-empty': {
    fontSize: theme.typography.label.fontSize,
    color: theme.colors.textMuted,
    fontStyle: 'italic',
    padding: `${theme.spacing.gapMd} 0`,
  },
  '& .crm-warn': {
    marginTop: theme.spacing.fieldGap,
    fontSize: theme.sizes.smallFontSize,
    color: theme.colors.danger,
    background: theme.colors.dangerBg,
    borderRadius: theme.radii.input,
    padding: `${theme.spacing.gapMd} ${theme.spacing.gapLg}`,
  },
});

function totesSuffix(totes) {
  return typeof totes === 'number' ? ` · ${totes} totes` : '';
}

function CarryoverRerouteModal({
  show,
  rerouting,
  skipping,
  onConfirm,
  onCancel,
}) {
  const autoCount = rerouting.filter((r) => r.mode === 'auto').length;
  const missingReason = skipping.some((s) => !s.reasonLabel);
  const confirmDisabled = missingReason || autoCount === 0;

  return (
    <Modal show={show} onHide={onCancel} backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Confirm carryover reroute</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <StyledDiv>
          <div className="crm-subtitle">
            Review what will and won&apos;t be replanned before sending to
            NextMV.
          </div>
          <div className="crm-group-label crm-group-label-reroute">
            Rerouting
          </div>
          {rerouting.length === 0 && (
            <div className="crm-empty" data-testid="crm-reroute-empty">
              None — every carryover stop is being held off.
            </div>
          )}
          {rerouting.map((r) => (
            <div
              className="crm-row"
              key={r.rowKey}
              data-testid={`crm-reroute-${r.rowKey}`}
            >
              <span>
                <strong>{r.name}</strong>
                {totesSuffix(r.totes)}
              </span>
              <span className="crm-row-right">
                {r.mode === 'auto' ? (
                  <span className="crm-tag-auto">
                    Reroute — route chosen by NextMV
                  </span>
                ) : (
                  <span className="crm-tag-specific">
                    → {r.targetRouteName || '—'} (staged in Route Editor)
                  </span>
                )}
              </span>
            </div>
          ))}
          <div className="crm-group-label crm-group-label-skip">
            Not rerouting (with reason)
          </div>
          {skipping.length === 0 && (
            <div className="crm-empty" data-testid="crm-skip-empty">
              None — all carryover stops are selected.
            </div>
          )}
          {skipping.map((s) => (
            <div
              className="crm-row"
              key={s.rowKey}
              data-testid={`crm-skip-${s.rowKey}`}
            >
              <span>
                <strong>{s.name}</strong>
                {totesSuffix(s.totes)}
              </span>
              <span className="crm-row-right">
                {s.reasonLabel ? (
                  <span className="crm-tag-specific">{s.reasonLabel}</span>
                ) : (
                  <span className="crm-tag-missing">⚠ reason required</span>
                )}
              </span>
            </div>
          ))}
          {missingReason && (
            <div className="crm-warn" data-testid="crm-warn">
              Every deselected stop needs a reason before you can reroute.
            </div>
          )}
        </StyledDiv>
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={onCancel} data-testid="crm-cancel">
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          disabled={confirmDisabled}
          className="btn-success"
          data-testid="crm-confirm"
        >
          Reroute {autoCount} stop{autoCount === 1 ? '' : 's'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

CarryoverRerouteModal.propTypes = {
  show: PropTypes.bool.isRequired,
  rerouting: PropTypes.arrayOf(
    PropTypes.shape({
      rowKey: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      totes: PropTypes.number,
      mode: PropTypes.oneOf(['auto', 'specific']).isRequired,
      targetRouteName: PropTypes.string,
    }),
  ),
  skipping: PropTypes.arrayOf(
    PropTypes.shape({
      rowKey: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      totes: PropTypes.number,
      reasonLabel: PropTypes.string,
    }),
  ),
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

CarryoverRerouteModal.defaultProps = {
  rerouting: [],
  skipping: [],
};

export default CarryoverRerouteModal;
